import './ReportPanel.css';

import React, { useMemo } from 'react';

import { Icon, ScrollableArea } from '@ohif/ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { NewFeatureBadge } from '../../AZmed/Components/NewFeatureBadge/NewFeatureBadge';
import { useStudyReport } from './useStudyReport';

/**
 * Release this panel ships in. The "New" badge shows while frontend/package.json
 * stays on the same major.minor, so patches keep it and the next minor drops it.
 */
export const REPORT_PANEL_RELEASE = '4.4.0';

/**
 * Sections of a report, in display order. `key` maps to the report JSON keys,
 * `title` is a translation key of the ReportPanel namespace.
 */
const REPORT_SECTIONS = [
  { id: 'indication', title: 'Indication', key: 'indication' },
  { id: 'technique', title: 'Technique', key: 'title_technique' },
  { id: 'results', title: 'Results', key: 'results_findings', asList: true },
  { id: 'conclusion', title: 'Conclusion', key: 'conclusion' },
];

/**
 * @param {string} patientName The patient name in DICOM format
 * @returns {string} The formatted patient name
 * @example formatPatientName('QUIMBY^JOE') => 'Quimby Joe'
 */
const formatPatientName = (patientName) => {
  if (!patientName) return '';

  return String(patientName)
    .replace(/[\^_]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * A sentence, ending either on the punctuation that opens the next one or on the
 * end of the string. The lookahead demands an upper case start, so a decimal point
 * (`49.0°`) is never read as a sentence end since a digit follows it. Written as a
 * match rather than a lookbehind split, which Safari only got in 16.4.
 */
const SENTENCE_PATTERN = /[\s\S]*?[.!?]+(?=\s+[A-ZÀ-Þ])|[\s\S]+/g;

/**
 * Findings are stored as a single string holding one sentence per finding, listed
 * one bullet each. Commas are not separators: they punctuate a finding.
 * @param {string} results
 * @returns {string[]} One entry per finding, its final period kept
 */
const splitFindings = (results) =>
  (String(results).match(SENTENCE_PATTERN) || [])
    .map((finding) => finding.trim())
    .filter(Boolean);

/**
 * Whether a section would put anything on screen. Asked of the rendered form and
 * not of the raw value, because the two can disagree: a findings string made of
 * blanks alone (`" "`) is a non-empty value that splits into no finding, and
 * testing the value would title a section above an empty list.
 * @param {Object} report The report as stored
 * @param {Object} section An entry of REPORT_SECTIONS
 * @returns {boolean}
 */
const hasRenderableContent = (report, { key, asList }) => {
  const value = report[key];

  if (!value) return false;

  return asList
    ? splitFindings(value).length > 0
    : Boolean(String(value).trim());
};

/**
 * Whether the object is shaped like a report at all: it names at least one of the
 * sections we know how to display, empty or not. What tells "a report we cannot
 * read" from "a report with nothing in it".
 * @param {Object} report The report as stored
 * @returns {boolean}
 */
const carriesKnownSection = (report) =>
  REPORT_SECTIONS.some((section) => section.key in report);

const ReportSection = ({ title, content, asList }) => (
  <div className="reportSection">
    <div className="reportSectionHeader">
      <div className="reportSectionTitle">{title}</div>
    </div>
    <div className="reportSectionContent">
      {asList ? (
        <ul className="reportFindings">
          {splitFindings(content).map((finding, index) => (
            <li key={index} className="reportFinding">
              {finding}
            </li>
          ))}
        </ul>
      ) : (
        <p className="reportText">{content}</p>
      )}
    </div>
  </div>
);

ReportSection.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  asList: PropTypes.bool,
};

/**
 * Stands in for the report body while loading, on error, or when the study has
 * none. One variant class per case so each can be styled on its own.
 */
const ReportPlaceholder = ({ variant, message, action }) => (
  <div className={`reportPanelPlaceholder reportPanelPlaceholder--${variant}`}>
    <Icon name="clipboard" width="28px" height="28px" />
    <div className="reportPanelPlaceholderText">{message}</div>
    {action && (
      <button
        type="button"
        className="reportPanelPlaceholderAction"
        onClick={action.onClick}
      >
        {action.label}
      </button>
    )}
  </div>
);

ReportPlaceholder.propTypes = {
  variant: PropTypes.oneOf(['loading', 'error', 'empty']).isRequired,
  message: PropTypes.string.isRequired,
  /** `{ label, onClick }`, only where the state is something the user can undo */
  action: PropTypes.object,
};

const ReportPanel = ({ studies = [], activeViewport }) => {
  const [t] = useTranslation('ReportPanel');

  const study = useMemo(() => {
    if (!studies.length) return undefined;

    const activeStudyInstanceUID =
      activeViewport && activeViewport.StudyInstanceUID;

    return (
      studies.find((s) => s.StudyInstanceUID === activeStudyInstanceUID) ||
      studies[0]
    );
  }, [studies, activeViewport]);

  // Handed the series on screen and not just its study: which report belongs next to
  // these images is a question only the displayed series settles, one study holding
  // as many reports as it holds languages and reprocessed versions.
  const { report, isLoading, error, retry } = useStudyReport(
    study,
    activeViewport && activeViewport.displaySetInstanceUID
  );

  const sections = useMemo(() => {
    if (!report) return [];

    return REPORT_SECTIONS.filter((section) =>
      hasRenderableContent(report, section)
    );
  }, [report]);

  // The dots and ellipsis in these keys only stay literal because the i18n
  // config sets keySeparator: false.
  const placeholder = useMemo(() => {
    if (isLoading) {
      return { variant: 'loading', message: t('Loading the report…') };
    }

    if (error) {
      // The effect only reruns on a new series, so without this the failure would
      // hold until the user visits another study and comes back.
      return {
        variant: 'error',
        message: t('The report could not be loaded.'),
        action: { label: t('Try again'), onClick: retry },
      };
    }

    // A report whose sections are all empty is not a failure: it is a document
    // that says nothing, and offering a retry would suggest something is broken
    // and could come back. Distinct from the absence below, which is about the
    // study having no report at all.
    if (report && carriesKnownSection(report)) {
      return {
        variant: 'empty',
        message: t('This report has no content.'),
      };
    }

    // Holding a report whose sections we do not recognise is not the same as
    // holding none: saying "none" would deny a clinical content we do have,
    // typically after the server added or renamed a field.
    if (report) {
      return {
        variant: 'error',
        message: t('The report could not be read.'),
      };
    }

    return {
      variant: 'empty',
      message: t('No report available for this study.'),
    };
  }, [isLoading, error, report, retry, t]);

  return (
    <div className="reportPanel">
      <div className="reportPanelHeader">
        <div className="reportPanelTitle">
          <Icon name="clipboard" width="16px" height="16px" />
          <span className="reportPanelTitleText">{t('Report')}</span>
          <NewFeatureBadge newInVersion={REPORT_PANEL_RELEASE} />
        </div>
        {study && (
          <div className="reportPanelPatient">
            <div className="reportPanelPatientName">
              {formatPatientName(study.PatientName)}
            </div>
          </div>
        )}
      </div>

      {sections.length ? (
        <ScrollableArea>
          <div className="reportPanelBody">
            {sections.map((section) => (
              <ReportSection
                key={section.id}
                title={t(section.title)}
                content={report[section.key]}
                asList={section.asList}
              />
            ))}
          </div>
        </ScrollableArea>
      ) : (
        <ReportPlaceholder {...placeholder} />
      )}
    </div>
  );
};

ReportPanel.propTypes = {
  studies: PropTypes.array,
  activeViewport: PropTypes.object,
};

export default ReportPanel;
